import {ArrowDownTrayIcon, ArrowPathIcon, PaperClipIcon} from "@heroicons/react/20/solid";
import {
  AttachmentCapabilities,
  AttachmentFile,
  AttachmentTarget,
  AttachmentTypeCode,
  AttachmentUploadItem,
} from "@ots-frontend/ots-data";
import {useEffect, useMemo, useState} from "react";
import {OtsActionButton, OtsAsyncActionButton} from "../components/OtsButton";
import {OtsInlineAlert} from "../components/OtsInlineAlert";
import {AttachmentDropzone} from "./AttachmentDropzone";
import {AttachmentList} from "./AttachmentList";
import {AttachmentUploadDraft, AttachmentUploadQueue} from "./AttachmentUploadQueue";
import {formatAttachmentSize} from "./attachment-utils";

export type AttachmentManagerProps = {
  attachments: AttachmentFile[];
  targets: AttachmentTarget[];
  capabilities?: Partial<AttachmentCapabilities>;
  /** Caller-selected attachment categories for this workflow. */
  attachmentTypes?: AttachmentTypeCode[];
  loading?: boolean;
  error?: string;
  title?: string;
  description?: string;
  bulkDownloadUrl?: string | null;
  onUpload?: (items: AttachmentUploadItem[]) => Promise<void> | void;
  onUpdate?: (attachment: AttachmentFile, changes: Pick<AttachmentFile, "attachmentType" | "description" | "displayOrder">) => Promise<void> | void;
  onDelete?: (attachment: AttachmentFile) => Promise<void> | void;
  onDownload?: (attachment: AttachmentFile) => void;
  onRefresh?: () => Promise<void> | void;
};

const defaultCapabilities: AttachmentCapabilities = {
  canView: true,
  canUpload: true,
  canEdit: true,
  canDelete: true,
  maxFileBytes: 100 * 1024 * 1024,
  maxFilesPerUpload: 20,
};

const defaultAttachmentTypes: AttachmentTypeCode[] = ["FILE"];
let draftSequence = 0;

const targetMatches = (a: AttachmentTarget, b: AttachmentTarget) =>
  a.objectId === b.objectId && a.objectType === b.objectType;

/** Complete attachment workflow; API operations are injected so it remains portable and Storybook-friendly. */
export const AttachmentManager = ({
  attachments,
  targets,
  capabilities: capabilityOverrides,
  attachmentTypes,
  loading = false,
  error,
  title = "Attachments",
  description = "Add supporting files and keep their details with the record.",
  bulkDownloadUrl,
  onUpload,
  onUpdate,
  onDelete,
  onDownload,
  onRefresh,
}: AttachmentManagerProps) => {
  const capabilities = useMemo(
    () => ({...defaultCapabilities, ...capabilityOverrides}),
    [capabilityOverrides]
  );
  const allowedTypes = useMemo(() => {
    const requested = attachmentTypes ?? capabilities.allowedAttachmentTypes ?? defaultAttachmentTypes;
    return capabilities.allowedAttachmentTypes && attachmentTypes
      ? requested.filter((type) => capabilities.allowedAttachmentTypes?.includes(type))
      : requested;
  }, [attachmentTypes, capabilities.allowedAttachmentTypes]);
  const [drafts, setDrafts] = useState<AttachmentUploadDraft[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<AttachmentTarget | undefined>(targets[0]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>();

  useEffect(() => {
    if (!selectedTarget || !targets.some((target) => targetMatches(target, selectedTarget))) {
      setSelectedTarget(targets[0]);
    }
  }, [selectedTarget, targets]);

  const addFiles = (files: File[]) => {
    if (!selectedTarget) return;
    const remaining = Math.max(0, (capabilities.maxFilesPerUpload ?? files.length) - drafts.filter(({status}) => status !== "complete").length);
    const accepted = files.slice(0, remaining);
    const overflow = files.slice(remaining);
    const contentTypes = capabilities.allowedContentTypes;

    const newDrafts = accepted.map((file): AttachmentUploadDraft => {
      let fileError: string | undefined;
      if (capabilities.maxFileBytes && file.size > capabilities.maxFileBytes) {
        fileError = `File is larger than the ${formatAttachmentSize(capabilities.maxFileBytes)} limit.`;
      } else if (contentTypes?.length && file.type && !contentTypes.includes(file.type)) {
        fileError = "This file type is not allowed.";
      }
      return {
        id: `attachment-draft-${++draftSequence}`,
        file,
        target: selectedTarget,
        metadata: {
          attachmentType: allowedTypes.includes("FILE") ? "FILE" : allowedTypes[0] ?? "FILE",
          description: "",
          reportable: true,
        },
        status: fileError ? "error" : "ready",
        error: fileError,
      };
    });

    if (overflow.length) {
      setUploadError(`Only ${capabilities.maxFilesPerUpload} files can be added at once. ${overflow.length} file${overflow.length === 1 ? " was" : "s were"} not added.`);
    } else {
      setUploadError(undefined);
    }
    setDrafts((current) => [...current, ...newDrafts]);
  };

  const updateDraft = (id: string, changes: Partial<Pick<AttachmentUploadDraft, "target" | "metadata">>) => {
    setDrafts((current) => current.map((draft) => draft.id === id ? {...draft, ...changes} : draft));
  };

  const upload = async () => {
    const ready = drafts.filter(({status, error: draftError}) => status === "ready" && !draftError);
    if (!ready.length || !onUpload) return;
    const ids = new Set(ready.map(({id}) => id));
    setUploading(true);
    setUploadError(undefined);
    setDrafts((current) => current.map((draft) => ids.has(draft.id) ? {...draft, status: "uploading", error: undefined} : draft));
    try {
      await onUpload(ready.map(({id, file, target, metadata}) => ({clientId: id, file, target, metadata})));
      setDrafts((current) => current.map((draft) => ids.has(draft.id) ? {...draft, status: "complete"} : draft));
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "The files could not be uploaded.";
      setUploadError(message);
      setDrafts((current) => current.map((draft) => ids.has(draft.id) ? {...draft, status: "error", error: message} : draft));
    } finally {
      setUploading(false);
    }
  };

  const readyCount = drafts.filter(({status, error: draftError}) => status === "ready" && !draftError).length;
  const completedCount = drafts.filter(({status}) => status === "complete").length;

  if (!capabilities.canView) {
    return <OtsInlineAlert tone="warning" title="Attachments unavailable">You do not have permission to view attachments for this record.</OtsInlineAlert>;
  }

  return (
    <div className="rounded-2xl border border-[var(--ots-outline-variant)] bg-[var(--ots-background)] p-4 text-[var(--ots-text)] shadow-sm sm:p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--ots-primary-container)] text-[var(--ots-primary)]">
            <PaperClipIcon aria-hidden="true" className="size-5"/>
          </span>
          <div>
            <h2 className="font-[family-name:var(--ots-font-display)] text-lg font-semibold text-[var(--ots-secondary)]">{title}</h2>
            <p className="mt-0.5 text-sm text-[var(--ots-text-muted)]">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <OtsActionButton size="compact" variant="text" icon={<ArrowPathIcon/>} disabled={loading} onClick={onRefresh}>Refresh</OtsActionButton>
          )}
          {bulkDownloadUrl && attachments.length > 1 && (
            <a
              href={bulkDownloadUrl}
              className="inline-flex min-h-8 items-center gap-2 rounded-lg px-2.5 py-1 text-sm font-semibold text-[var(--ots-primary)] hover:bg-[var(--ots-primary-container)] focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]">
              <ArrowDownTrayIcon aria-hidden="true" className="size-[18px]"/> Download all
            </a>
          )}
        </div>
      </header>

      <div className="mt-5 space-y-5">
        {error && <OtsInlineAlert tone="danger" title="Attachments could not be loaded">{error}</OtsInlineAlert>}
        {uploadError && <OtsInlineAlert tone="danger" title="Upload failed" onDismiss={() => setUploadError(undefined)}>{uploadError}</OtsInlineAlert>}

        {capabilities.canUpload && onUpload && targets.length > 0 && (
          <section aria-label="Add attachments" className="space-y-3">
            {targets.length > 1 && (
              <label className="block max-w-sm text-xs font-medium text-[var(--ots-text-muted)]">
                Add files to
                <select
                  className="mt-1 min-h-9 w-full rounded-lg border border-[var(--ots-field-outline)] bg-[var(--ots-field)] px-2.5 text-sm text-[var(--ots-text)] outline-none focus:border-[var(--ots-primary)] focus:ring-2 focus:ring-[var(--ots-primary-container)]"
                  value={selectedTarget ? `${selectedTarget.objectType}:${selectedTarget.objectId}` : ""}
                  onChange={(event) => setSelectedTarget(targets.find((target) => `${target.objectType}:${target.objectId}` === event.target.value))}>
                  {targets.map((target) => (
                    <option key={`${target.objectType}:${target.objectId}`} value={`${target.objectType}:${target.objectId}`}>
                      {target.label ?? `${target.objectType} ${target.objectId}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <AttachmentDropzone
              compact={attachments.length > 0 || drafts.length > 0}
              disabled={uploading}
              accept={capabilities.allowedContentTypes}
              onFiles={addFiles}/>
          </section>
        )}

        <AttachmentUploadQueue
          items={drafts}
          targets={targets}
          attachmentTypes={allowedTypes}
          disabled={uploading}
          onChange={updateDraft}
          onRemove={(id) => setDrafts((current) => current.filter((draft) => draft.id !== id))}/>

        {drafts.length > 0 && (
          <div className="flex flex-wrap justify-end gap-2 border-b border-[var(--ots-outline-variant)] pb-5">
            {completedCount > 0 && (
              <OtsActionButton variant="text" onClick={() => setDrafts((current) => current.filter(({status}) => status !== "complete"))}>Clear uploaded</OtsActionButton>
            )}
            <OtsAsyncActionButton
              variant="primary"
              isLoading={uploading}
              loadingLabel="Uploading…"
              disabled={!readyCount}
              onClick={upload}>
              Upload {readyCount} {readyCount === 1 ? "file" : "files"}
            </OtsAsyncActionButton>
          </div>
        )}

        {loading ? (
          <div role="status" aria-label="Loading attachments" className="space-y-2">
            {[0, 1, 2].map((item) => <div key={item} className="h-16 animate-pulse rounded-xl bg-[var(--ots-surface)]"/>)}
          </div>
        ) : (
          <AttachmentList
            attachments={attachments}
            targets={targets}
            capabilities={capabilities}
            attachmentTypes={allowedTypes}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onDownload={onDownload}/>
        )}
      </div>
    </div>
  );
};
